import React, { useContext, useEffect, useState, useCallback } from 'react';
import api from '../api';
import { SelectedCompanyContext } from '../contexts/SelectedCompanyContext';
import { ModernCard, ModernButton, ModernProgressRing, ModernBadge, ModernSelect, getStatusVariant } from './ui/modern';

const INTERNAL_SYSTEMS = ['PSA','AEM','Fincon','IT Glue'];

function getReportStatusVariant(status) {
  if (status === 'Not Started') return 'pending';
  return getStatusVariant(status);
}

function getPlannedUsers(branch, onboard) {
  return branch?.numberOfUsers ?? onboard?.users?.length ?? 0;
}

// BranchDevice rows represent BOTH "Network Devices" + "Other Devices" (we initialize with totalDevices)
function getPlannedDevices(branch, onboard) {
  const plannedOther = branch?.numberOfDevices;
  const plannedNetwork = branch?.numberOfNetworkDevices;

  if (plannedOther == null && plannedNetwork == null) {
    return onboard?.devices?.length ?? 0;
  }

  return (plannedOther ?? 0) + (plannedNetwork ?? 0);
}

const ReportPage = ({ user }) => {
  const { selected, setSelected } = useContext(SelectedCompanyContext);
  const [companies,setCompanies]=useState([]);
  const [companyId,setCompanyId]=useState(null);
  const [branchesData,setBranchesData]=useState({});
  const [preview,setPreview]=useState(null);
  const [dynamicTypes, setDynamicTypes] = useState([]);
  const [dynamicInstances, setDynamicInstances] = useState({});

  // load companies list
  useEffect(()=>{
    const load=async()=>{
      try{
        const list = await api.get('/companies').then(r=>r.data);
        setCompanies(list);
        if(user.role==='Client'){
          const comp = list.find(c=>c.id === user.companyId);
          setCompanyId(comp?.id||null);
        }
      }catch(e){console.error('fetch companies',e);}  };
    load();
  },[user]);

  const fetchCompanyDetails = useCallback(async () => {
    if(!companyId) return;
    try{
      const comp = await api.get(`/companies/${companyId}`).then(r=>r.data);
      setSelected(comp);

      // Fetch holistic progress from the single source of truth
      try {
        const prev = await api.get(`/reports/company/${companyId}/preview`).then(r => r.data);
        setPreview(prev || null);
      } catch (e) {
        console.error('preview fetch', e);
        setPreview(null);
      }

      // fetch branch onboarding data
      const bData={};
      for(const b of comp.Branches||[]){
        try{
          bData[b.id] = await api.get(`/branches/${b.id}/onboarding`).then(r=>r.data);
        }catch(e){console.error('branch onboard fetch',e);}
      }
      setBranchesData(bData);

      // fetch dynamic entity types + instances
      try {
        const types = await api.get('/entity-types', { params: { sectorId: comp.sectorId, scope: 'branch' } }).then(r => r.data);
        setDynamicTypes(types || []);
        const instancesByBranch = {};
        for (const b of comp.Branches || []) {
          instancesByBranch[b.id] = {};
          for (const t of types || []) {
            try {
              const rows = await api.get('/entity-instances', { params: { entityTypeId: t.id, branchId: b.id } }).then(r => r.data);
              instancesByBranch[b.id][t.id] = rows || [];
            } catch (e) {
              console.error('entity instances fetch', e);
            }
          }
        }
        setDynamicInstances(instancesByBranch);
      } catch (e) {
        console.error('entity types fetch', e);
      }
    }catch(e){console.error('company fetch',e);}
  }, [companyId, setSelected]);

  // initial load
  useEffect(()=>{ fetchCompanyDetails(); },[fetchCompanyDetails]);

  // NEW: auto-refresh the data every 30 seconds while the component is mounted
  useEffect(() => {
    // only start the timer if a company has been selected
    if (!companyId) return;
    const timer = setInterval(() => {
      fetchCompanyDetails();
    }, 30000); // 30 000 ms = 30 s
    return () => clearInterval(timer);
  }, [companyId, fetchCompanyDetails]);

  useEffect(() => {
    if(!companyId && selected){
      setCompanyId(selected.id);
    }
  }, [selected, companyId]);

  if(user.role==='Client' && !companyId){ 
    return (
      <div className="flex items-center justify-center py-16">
        <ModernCard>
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <div className="text-gray-900 dark:text-white text-lg">Loading...</div>
          </div>
        </ModernCard>
      </div>
    );
  }

  const company = selected;
  if(!company) return (
    <div className="relative z-10 py-2">
      <ModernCard className="text-center">
        <div className="mb-8">
          <div className="text-6xl mb-4">📊</div>
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">Reports</h1>
          <p className="text-gray-600 dark:text-slate-400">Select a company to view onboarding report</p>
        </div>
        {user.role!=='Client' && (
          <div className="max-w-md mx-auto">
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-400 mb-2">Choose Company</label>
            <ModernSelect
              value={companyId ?? ''}
              onChange={e=>setCompanyId(parseInt(e.target.value)||null)}
              options={[
                { value: '', label: '-- Select company --' },
                ...companies.map(c => ({ value: c.id, label: c.name }))
              ]}
            />
          </div>
        )}
      </ModernCard>
    </div>
  );

  const sysTasks = (company.CompanyTasks||[]).filter(t=>INTERNAL_SYSTEMS.includes(t.type));
  const otherTasks = (company.CompanyTasks||[]).filter(t=>!INTERNAL_SYSTEMS.includes(t.type) && !t.branchId);

  // ------ progress calculations ------
  // Overall company progress (internal + company tasks + all branch tasks + server onboarding)
  let totalTasks = sysTasks.length + otherTasks.length;
  let completedTasks = [...sysTasks, ...otherTasks].filter(t=>t.status==='Completed' || t.completed).length;

  // include server onboarding items (if any)
  const servers = company.CompanyServers || [];
  totalTasks += servers.length;
  completedTasks += servers.filter(s => s.completed).length;

  // include per-branch tasks
  for(const bId in branchesData){
     const bData=branchesData[bId];
     const branchMeta = company.Branches?.find(br=> br.id===Number(bId));
     const plannedUsers = getPlannedUsers(branchMeta, bData);
     const plannedDevices = getPlannedDevices(branchMeta, bData);
     totalTasks += plannedUsers + plannedDevices;
     completedTasks += (bData.users?.filter(u=>u.completed).length||0) + (bData.devices?.filter(d=>d.completed).length||0);
  }

  const previewTotal = typeof preview?.progress?.total === 'number' ? preview.progress.total : null;
  const previewCompleted = typeof preview?.progress?.completed === 'number' ? preview.progress.completed : null;
  const previewPct = typeof preview?.progress?.percentage === 'number' ? preview.progress.percentage : null;

  const overallPct = previewPct ?? (totalTasks? Math.round((completedTasks/totalTasks)*100):0);
  const overallTotal = previewTotal ?? totalTasks;
  const overallDone = previewCompleted ?? completedTasks;

  const previewByBranchId = {};
  for (const b of preview?.branches || []) {
    previewByBranchId[b.id] = b;
  }

  return (
    <div className="relative z-10 py-2">
      <div className="py-2">
        {/* Header Card */}
        <ModernCard className="mb-8">
          <div className="text-center">
            <div className="text-5xl mb-4">📊</div>
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">{company.name}</h1>
            <p className="text-gray-600 dark:text-slate-400 mb-6">Onboarding Progress Report</p>
            <div className="flex justify-center gap-3 flex-wrap">
              <ModernButton variant="glass" onClick={fetchCompanyDetails}>
                🔄 Refresh Data
              </ModernButton>
              <ModernButton 
                variant="gradient"
                onClick={() => {
                  const token = localStorage.getItem('token');
                  window.open(`/api/reports/company/${companyId}?token=${token}`, '_blank');
                }}
              >
                📥 Download PDF
              </ModernButton>
            </div>
          </div>
        </ModernCard>

        {/* Progress Overview */}
        <ModernCard className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Progress Overview</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Overall Progress */}
            <div className="flex flex-col items-center">
              <ModernProgressRing percentage={overallPct} size={140} />
              <div className="text-center mt-4">
                <div className="text-lg font-bold text-gray-900 dark:text-white">Company Overall</div>
                <div className="text-sm text-gray-600 dark:text-slate-400">{overallDone}/{overallTotal} tasks</div>
              </div>
            </div>
            
            {/* Branch Progress */}
            {company.Branches && company.Branches.map(b=>{
              const data = branchesData[b.id]||{users:[],devices:[]};

              const p = previewByBranchId[b.id];
              const done = typeof p?.completed === 'number'
                ? p.completed
                : (data.users?.filter(u=>u.completed).length||0)+(data.devices?.filter(d=>d.completed).length||0);
              const total = typeof p?.total === 'number'
                ? p.total
                : (getPlannedUsers(b, data) + getPlannedDevices(b, data));
              const pct = typeof p?.percentage === 'number'
                ? p.percentage
                : (total ? Math.min(100, Math.round((done / total) * 100)) : 0);
              return (
                <div key={b.id} className="flex flex-col items-center">
                  <ModernProgressRing percentage={pct} size={120} />
                  <div className="text-center mt-4">
                    <div className="text-base font-bold text-gray-900 dark:text-white">{b.name}</div>
                    <div className="text-sm text-gray-600 dark:text-slate-400">{done}/{total} tasks</div>
                  </div>
                </div>
              );
            })}
          </div>
        </ModernCard>

        {/* Company-wide Tasks */}
        <ModernCard className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Company-wide Tasks</h2>
          {otherTasks.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-5xl mb-3">📋</div>
              <div className="text-gray-600 dark:text-slate-400">No company-wide tasks captured</div>
            </div>
          ) : (
            <div>
              {/* Desktop: Table View */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-white/10">
                      <th className="text-left py-3 px-4 text-gray-600 dark:text-slate-400 font-medium">Task</th>
                      <th className="text-center py-3 px-4 text-gray-600 dark:text-slate-400 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {otherTasks.map(t => (
                      <tr key={t.id} className="border-b border-gray-200 dark:border-white/5 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors">
                        <td className="py-3 px-4 text-gray-900 dark:text-white">{t.type}</td>
                        <td className="py-3 px-4 text-center">
                          <ModernBadge variant={getReportStatusVariant(t.status)}>
                            {t.status}
                          </ModernBadge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile: Card View */}
              <div className="md:hidden space-y-3">
                {otherTasks.map(t => (
                  <div 
                    key={t.id} 
                    className="flex items-center justify-between gap-3 p-4 bg-white dark:bg-slate-800/90 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm"
                  >
                    <span className="text-gray-900 dark:text-slate-100 font-medium flex-1 min-w-0 whitespace-normal leading-snug">
                      {t.type}
                    </span>
                    <ModernBadge variant={getReportStatusVariant(t.status)}>
                      {t.status}
                    </ModernBadge>
                  </div>
                ))}
              </div>
            </div>
          )}
        </ModernCard>

        {/* Server Info */}
        {company.hasServers && company.CompanyServers && (
          <ModernCard className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">🖥️ Server Information – {company.serverLocation}</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-white/10">
                    <th className="text-left py-3 px-4 text-gray-600 dark:text-slate-400 font-medium">Name</th>
                    <th className="text-left py-3 px-4 text-gray-600 dark:text-slate-400 font-medium">IP Address</th>
                    <th className="text-left py-3 px-4 text-gray-600 dark:text-slate-400 font-medium">Domain</th>
                    <th className="text-left py-3 px-4 text-gray-600 dark:text-slate-400 font-medium">Roles</th>
                    <th className="text-center py-3 px-4 text-gray-600 dark:text-slate-400 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {company.CompanyServers.map(s => (
                    <tr key={s.id} className="border-b border-gray-200 dark:border-white/5 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors">
                      <td className="py-3 px-4 text-gray-900 dark:text-white">{s.serverName||'-'}</td>
                      <td className="py-3 px-4 text-gray-700 dark:text-slate-300">{s.ipAddress||'-'}</td>
                      <td className="py-3 px-4 text-gray-700 dark:text-slate-300">{s.domain||'-'}</td>
                      <td className="py-3 px-4 text-gray-700 dark:text-slate-300">{s.serverRoles||'-'}</td>
                      <td className="py-3 px-4 text-center">
                        <ModernBadge variant={s.completed ? 'success' : 'warning'}>
                          {s.completed ? 'Completed' : 'In Progress'}
                        </ModernBadge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </ModernCard>
        )}

        {/* Branch Sections */}
        <div className="space-y-6">
          {company.Branches && company.Branches.map(branch=>{
            const onboard = branchesData[branch.id]||{users:[],devices:[]};
            const plannedUsers = getPlannedUsers(branch, onboard);
            const plannedDevices = getPlannedDevices(branch, onboard);
            const userDone = onboard.users.filter(u=>u.completed).length;
            const devDone = onboard.devices.filter(d=>d.completed).length;

            const renderUserRow = (u)=>{
               if(!u.completed) return null;
               const noteLines = (u.notes||'').split('\n').filter(Boolean);
               return (
                 <tr key={u.id} className="border-b border-gray-200 dark:border-white/5 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors">
                   <td className="py-3 px-4 text-gray-900 dark:text-white">{u.userName||'-'}</td>
                   <td className="py-3 px-4 text-center">
                     <ModernBadge variant="success">Completed</ModernBadge>
                   </td>
                   <td className="py-3 px-4 text-gray-700 dark:text-slate-300 text-sm">
                     {noteLines.length ? (
                       <div className="space-y-1">
                         {noteLines.map((ln,i)=>(<div key={i}>• {ln}</div>))}
                       </div>
                     ) : '-'}
                   </td>
                 </tr>
               );
            };

            const renderDeviceRow = (d)=>{
               if(!d.completed) return null;
               const noteLines = (d.notes||'').split('\n').filter(Boolean);
               return (
                 <tr key={d.id} className="border-b border-gray-200 dark:border-white/5 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors">
                   <td className="py-3 px-4 text-gray-900 dark:text-white">{d.deviceName||'-'}</td>
                   <td className="py-3 px-4 text-center">
                     <ModernBadge variant="success">Completed</ModernBadge>
                   </td>
                   <td className="py-3 px-4 text-gray-700 dark:text-slate-300 text-sm">
                     {noteLines.length ? (
                       <div className="space-y-1">
                         {noteLines.map((ln,i)=>(<div key={i}>• {ln}</div>))}
                       </div>
                     ) : '-'}
                   </td>
                 </tr>
               );
            };

            return (
              <ModernCard key={branch.id}>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                  <span className="text-3xl">🏢</span>
                  {branch.name}
                </h3>

                {/* Users Section */}
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                      <span>👥</span>
                      User Workstation Onboarding
                    </h4>
                    <ModernBadge variant={userDone === plannedUsers ? 'success' : 'warning'}>
                      {userDone}/{plannedUsers} completed
                    </ModernBadge>
                  </div>
                  {onboard.users.filter(u=>u.completed).length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-200 dark:border-white/10">
                            <th className="text-left py-3 px-4 text-gray-600 dark:text-slate-400 font-medium">User</th>
                            <th className="text-center py-3 px-4 text-gray-600 dark:text-slate-400 font-medium">Status</th>
                            <th className="text-left py-3 px-4 text-gray-600 dark:text-slate-400 font-medium">Notes</th>
                          </tr>
                        </thead>
                        <tbody>
                          {onboard.users.map(renderUserRow)}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-6 text-gray-600 dark:text-slate-400">
                      No completed users yet
                    </div>
                  )}
                </div>

                {/* Devices Section */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                      <span>🌐</span>
                      Network Device Onboarding
                    </h4>
                    <ModernBadge variant={devDone === plannedDevices ? 'success' : 'warning'}>
                      {devDone}/{plannedDevices} completed
                    </ModernBadge>
                  </div>
                  {onboard.devices.filter(d=>d.completed).length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-200 dark:border-white/10">
                            <th className="text-left py-3 px-4 text-gray-600 dark:text-slate-400 font-medium">Device</th>
                            <th className="text-center py-3 px-4 text-gray-600 dark:text-slate-400 font-medium">Status</th>
                            <th className="text-left py-3 px-4 text-gray-600 dark:text-slate-400 font-medium">Notes</th>
                          </tr>
                        </thead>
                        <tbody>
                          {onboard.devices.map(renderDeviceRow)}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-6 text-gray-600 dark:text-slate-400">
                      No completed devices yet
                    </div>
                  )}
                </div>

                {/* Dynamic Entity Sections */}
                {dynamicTypes.filter(t => !['branch_user','branch_device'].includes(t.code)).map((t) => {
                  const rows = (dynamicInstances[branch.id] && dynamicInstances[branch.id][t.id]) || [];
                  if (!rows.length) return null;
                  return (
                    <div key={`dynamic-${t.id}`} className="mt-8">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                          <span>{t.icon || '📦'}</span>
                          {t.name}
                        </h4>
                        <ModernBadge variant={rows.every(r => r.completed) ? 'success' : 'warning'}>
                          {rows.filter(r => r.completed).length}/{rows.length} completed
                        </ModernBadge>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-gray-200 dark:border-white/10">
                              <th className="text-left py-3 px-4 text-gray-600 dark:text-slate-400 font-medium">Item</th>
                              <th className="text-center py-3 px-4 text-gray-600 dark:text-slate-400 font-medium">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rows.map((r) => (
                              <tr key={`dynamic-row-${r.id}`} className="border-b border-gray-100 dark:border-white/5">
                                <td className="py-3 px-4 text-gray-900 dark:text-white">{r.displayName || `${t.name} #${r.id}`}</td>
                                <td className="py-3 px-4 text-center">
                                  <ModernBadge variant={r.completed ? 'success' : 'warning'}>
                                    {r.completed ? 'Completed' : 'In Progress'}
                                  </ModernBadge>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })}
              </ModernCard>
            );
          })}
        </div>

        {/* Company Selector */}
        {user.role!=='Client' && (
          <ModernCard className="mt-8">
            <div className="text-center">
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-400 mb-3">Switch Company</label>
              <div className="max-w-md mx-auto">
                <ModernSelect
                  value={companyId ?? ''}
                  onChange={e=>setCompanyId(parseInt(e.target.value)||null)}
                  options={companies.map(c => ({ value: c.id, label: c.name }))}
                />
              </div>
            </div>
          </ModernCard>
        )}
      </div>
    </div>
  );
};

export default ReportPage; 